/**
 * 환생/패배 전환 오버레이 (Humble View) — 우주선 홀로그램·터미널 블랙아웃 연출.
 *
 * "언제 무엇을"은 systems/rebirthTransitionTimeline.ts(순수 SSoT)가 소유하고,
 * 여기는 step을 tween/delayedCall로 재생만 한다. 덮는 범위는 게임뷰 outer 전체
 * (전투 프레임 포함, 상단 HUD/하단 탭은 가리지 않는다). 재생 중 게임뷰 포인터는
 * rect가 swallow하고, 환생 버튼 재클릭은 isPlaying 가드가 막는다.
 *
 * 시각 장식(스캐너 프레임·시그널 바·스캔라인·터미널 링)은 모두 sceneVisibleGameviewRect
 * 안에 바운드되며, destroy()에서 전부 정리된다.
 *
 * 객체는 play()마다 생성하고 done/destroy에서 정리한다 — 환생은 드문 이벤트라
 * 상주 객체를 두지 않는다. scene SHUTDOWN 시에는 boot disposer가 destroy()를 호출한다.
 */
import Phaser from "phaser";
import { t } from "../i18n/i18n.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { DEPTH } from "../objects/depthLayers.js";
import { sceneVisibleGameviewRect } from "./sceneLayout.js";
import { FONT_FAMILY, TEXT_RESOLUTION, TYPO } from "./uiTokens.js";
import { REBIRTH_TRANSITION } from "../systems/rebirthTransitionTimeline.js";

const Verbose = false;

export interface RebirthTransitionOverlayOptions {
  readonly texts?: readonly [string, string];
  readonly onDone?: () => void;
}

// 장식 레이아웃 상수.
const CORNER_LEN = 18;
const CORNER_THICK = 2;
const SIGNAL_BAR_COUNT = 5;
const SIGNAL_BAR_W = 3;
const SIGNAL_BAR_GAP = 2;
const SIGNAL_MARGIN = 10;
const RING_RADIUS_MAX = 32;
const RING_THICK = 1.5;
const SCANLINE_GAP = 4;
const SCANLINE_ALPHA = 0.07;

export class RebirthTransitionOverlay {
  private cover?: Phaser.GameObjects.Rectangle;
  private text?: Phaser.GameObjects.Text;
  private timers: Phaser.Time.TimerEvent[] = [];
  private decorations: Phaser.GameObjects.GameObject[] = [];
  private playing = false;

  constructor(private readonly scene: Phaser.Scene) {}

  get isPlaying(): boolean {
    return this.playing;
  }

  /** 연출 재생 — onReset은 타임라인의 reset 시점(완전 암전 중)에 1회 호출된다. */
  play(onReset: () => void, opts: RebirthTransitionOverlayOptions = {}): void {
    if (this.playing) return;
    this.playing = true;
    const s = this.scene;
    const gv = sceneVisibleGameviewRect(s);
    let showTextIndex = 0;
    if (Verbose) console.log("[RebirthTransitionOverlay][V][play] 연출 시작");

    const cx = gv.outerX + gv.outerW / 2;
    const cy = gv.outerTop + gv.outerH / 2;
    const baseDepth = DEPTH.rebirthTransitionCover;
    const decoDepth = baseDepth + 0.5;
    const textDepth = DEPTH.rebirthTransitionText;

    // 커버는 t=0부터 불투명 + interactive: 전투 화면을 완전히 가리고 포인터를 swallow한다.
    this.cover = s.add
      .rectangle(gv.outerX, gv.outerTop, gv.outerW, gv.outerH, PALETTE_NUM.overlayDim)
      .setOrigin(0)
      .setAlpha(1)
      .setDepth(baseDepth)
      .setInteractive();

    const textY = Math.min(
      gv.outerTop + gv.outerH - 20,
      cy + Math.min(52, Math.max(18, gv.outerH * 0.22))
    );
    const ringRadius = Math.min(
      RING_RADIUS_MAX,
      Math.max(12, Math.min(gv.outerW, gv.outerH) * 0.18)
    );

    // 안내 텍스트.
    this.text = s.add
      .text(cx, textY, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.subtitle,
        color: PALETTE.infoBlue,
        align: "center",
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setResolution(TEXT_RESOLUTION)
      .setDepth(textDepth);

    // 장식: 스캐너 코너 레일.
    const frameInset = 8;
    const fl = gv.outerX + frameInset;
    const fr = gv.outerX + gv.outerW - frameInset;
    const ft = gv.outerTop + frameInset;
    const fb = gv.outerTop + gv.outerH - frameInset;
    const corners = [
      // top-left
      { x: fl, y: ft, dx: CORNER_LEN, dy: 0 },
      { x: fl, y: ft, dx: 0, dy: CORNER_LEN },
      // top-right
      { x: fr, y: ft, dx: -CORNER_LEN, dy: 0 },
      { x: fr, y: ft, dx: 0, dy: CORNER_LEN },
      // bottom-left
      { x: fl, y: fb, dx: CORNER_LEN, dy: 0 },
      { x: fl, y: fb, dx: 0, dy: -CORNER_LEN },
      // bottom-right
      { x: fr, y: fb, dx: -CORNER_LEN, dy: 0 },
      { x: fr, y: fb, dx: 0, dy: -CORNER_LEN },
    ];
    for (const c of corners) {
      const line = s.add
        .line(0, 0, c.x, c.y, c.x + c.dx, c.y + c.dy, PALETTE_NUM.infoBlue)
        .setOrigin(0)
        .setLineWidth(CORNER_THICK)
        .setAlpha(0)
        .setDepth(decoDepth);
      this.decorations.push(line);
    }

    // 장식: 시그널 바(우상단).
    for (let i = 0; i < SIGNAL_BAR_COUNT; i++) {
      const barH = 4 + i * 3;
      const bx = fr - SIGNAL_MARGIN - (SIGNAL_BAR_COUNT - i) * (SIGNAL_BAR_W + SIGNAL_BAR_GAP);
      const by = ft + SIGNAL_MARGIN + (4 + (SIGNAL_BAR_COUNT - 1) * 3) - barH;
      const bar = s.add
        .rectangle(bx, by, SIGNAL_BAR_W, barH, PALETTE_NUM.infoBlue)
        .setOrigin(0)
        .setAlpha(0)
        .setDepth(decoDepth);
      this.decorations.push(bar);
    }

    // 장식: 중앙 터미널 링.
    const ring = s.add
      .circle(cx, cy - 8, ringRadius, PALETTE_NUM.appBackground, 0)
      .setStrokeStyle(RING_THICK, PALETTE_NUM.infoBlue, 0.6)
      .setAlpha(0)
      .setDepth(decoDepth);
    this.decorations.push(ring);

    const ringInner = s.add
      .circle(cx, cy - 8, ringRadius * 0.55, PALETTE_NUM.appBackground, 0)
      .setStrokeStyle(RING_THICK * 0.7, PALETTE_NUM.infoBlue, 0.3)
      .setAlpha(0)
      .setDepth(decoDepth);
    this.decorations.push(ringInner);

    // 장식: 스캔라인(가로 줄무늬).
    const scanlines: Phaser.GameObjects.Rectangle[] = [];
    const scanlineCount = Math.floor(gv.outerH / SCANLINE_GAP);
    for (let i = 0; i < scanlineCount; i++) {
      const ly = gv.outerTop + i * SCANLINE_GAP;
      const sl = s.add
        .rectangle(gv.outerX, ly, gv.outerW, 1, PALETTE_NUM.infoBlue)
        .setOrigin(0)
        .setAlpha(0)
        .setDepth(decoDepth);
      scanlines.push(sl);
      this.decorations.push(sl);
    }

    // 타임라인 재생.
    for (const step of REBIRTH_TRANSITION) {
      this.timers.push(
        s.time.delayedCall(step.at, () => {
          // destroy() 직후 같은 프레임에 발화할 수 있는 잔존 타이머 방어 (TimerEvent.remove 특성).
          if (!this.playing) return;
          switch (step.kind) {
            case "fadeOut": {
              const durationMs = step.durationMs ?? 0;
              // 커버는 이미 불투명하므로 전투 화면은 시작 프레임부터 보이지 않는다.
              for (const deco of this.decorations) {
                const isScanline = scanlines.includes(deco as Phaser.GameObjects.Rectangle);
                s.tweens.add({
                  targets: deco,
                  alpha: isScanline ? SCANLINE_ALPHA : 0.7,
                  duration: durationMs + 100,
                  ease: "Quad.easeOut",
                });
              }
              s.tweens.add({
                targets: ring,
                angle: 360,
                duration: 4000,
                repeat: -1,
                ease: "Linear",
              });
              s.tweens.add({
                targets: ringInner,
                angle: -360,
                duration: 6000,
                repeat: -1,
                ease: "Linear",
              });
              break;
            }
            case "showText":
              this.text?.setText(opts.texts?.[showTextIndex++] ?? t(step.textKey ?? "")).setAlpha(0);
              s.tweens.add({ targets: this.text, alpha: 1, duration: step.durationMs });
              break;
            case "reset":
              if (Verbose) console.log("[RebirthTransitionOverlay][V][play] 암전 중 리셋 실행");
              onReset();
              break;
            case "fadeIn":
              s.tweens.add({
                targets: [this.cover, this.text, ...this.decorations],
                alpha: 0,
                duration: step.durationMs,
                ease: "Quad.easeIn",
              });
              break;
            case "done":
              this.destroyObjects();
              opts.onDone?.();
              break;
          }
        }),
      );
    }
  }

  /** 강제 정리 (scene SHUTDOWN disposer) — 진행 중 tween/타이머까지 제거. */
  destroy(): void {
    this.destroyObjects();
  }

  private destroyObjects(): void {
    for (const timer of this.timers) timer.remove(false);
    this.timers = [];
    if (this.cover) this.scene.tweens.killTweensOf(this.cover);
    if (this.text) this.scene.tweens.killTweensOf(this.text);
    for (const deco of this.decorations) {
      this.scene.tweens.killTweensOf(deco);
      (deco as Phaser.GameObjects.Shape).destroy();
    }
    this.decorations = [];
    this.cover?.destroy();
    this.text?.destroy();
    this.cover = undefined;
    this.text = undefined;
    this.playing = false;
  }
}
